import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import BackButton from "../components/BackButton";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { clearActowizRequest, fetchActowizFeatures } from "../store/actowiz/actowizSlice";
import { apiRequest } from "../api/client";

function FeaturesProducts({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const items = useAppSelector((s) => s.actowiz.features);
  const status = useAppSelector((s) => s.actowiz.featuresStatus);
  const error = useAppSelector((s) => s.actowiz.featuresError);

  const [page, setPage] = useState(1);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const pageSize = 20;

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    const start = (page - 1) * pageSize;
    dispatch(
      fetchActowizFeatures({
        start,
        offset: pageSize,
        mode: "replace",
      })
    );
  }, [page, pageSize, dispatch]);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  const getRequestKey = (targetPage) => `features_v2:${(targetPage - 1) * pageSize}:${pageSize}`;
  const hasPrev = page > 1;
  const hasNext = items.length >= pageSize;

  const handleAddToList = useCallback(
    async (product) => {
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists.");
      }

      const lists = Array.isArray(minimalRes.data)
        ? minimalRes.data
        : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

      if (!lists.length) throw new Error("No grocery list found. Please create a list first.");

      let selectedListId = null;
      if (lists.length === 1) {
        selectedListId = lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
      } else {
        const selected = await chooseList(lists);
        if (!selected) {
          const cancelError = new Error("ADD_TO_LIST_CANCELLED");
          cancelError.code = "ADD_TO_LIST_CANCELLED";
          throw cancelError;
        }
        selectedListId = selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
      }
      if (!selectedListId) throw new Error("Unable to select grocery list.");

      const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
      if (!productId) throw new Error("Invalid product.");

      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: {
          product_id: String(productId),
          image_url: product?.images?.[0] || product?.image || product?.image_url || "",
          price: product?.price ?? "",
          weight: product?.packsize || product?.weight || "",
          quantity: 1,
        },
      });
      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }
    },
    [chooseList]
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 md:px-6">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex items-end justify-between gap-4">
          <div>
            <BackButton className="mb-3" />
            <h1 className="text-2xl font-extrabold text-[#24303A] md:text-3xl">
              Features Products
            </h1>
            <p className="mt-1 text-sm text-[#6E7B86]">
              Browse featured products.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              disabled={!hasPrev || status === "loading"}
              onClick={() =>
                setPage((v) => {
                  const nextPage = Math.max(1, v - 1);
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-slate-500">Page {page}</span>
            <button
              type="button"
              disabled={!hasNext || status === "loading"}
              onClick={() =>
                setPage((v) => {
                  const nextPage = v + 1;
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
              className="rounded-full bg-[#7352C7] px-3 py-1.5 font-semibold text-white disabled:opacity-50 shadow-sm"
            >
              Next →
            </button>
          </div>
        </div>

        {status === "loading" && (
          <div className="flex justify-center py-16">
            <span className="text-sm text-slate-500">Loading…</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-line">
            {error}
          </div>
        )}

        {!error && status !== "loading" && items.length === 0 && (
          <div className="flex justify-center py-16">
            <span className="text-sm text-slate-500">No products found.</span>
          </div>
        )}

        {status !== "loading" && items.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-5">
            {items.map((product, idx) => (
              <ProductCard
                key={`${product.pid || product.sku}-${idx}`}
                accent={["orange", "blue", "green", "purple"][idx % 4]}
                product={product}
                onAddToList={handleAddToList}
                onClick={() =>
                  navigate(`/product/${encodeURIComponent(product.pid || product.sku)}`)
                }
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-xs text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-sm font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeaturesProducts;

